import type { ComponentProps } from 'react'
import { useCallback, useState } from 'react'

import css from './ModalNav.css'
import { ModalNavGroup } from './ModalNavGroup'
import type { ModalErrors, ModalGroup, ModalItem, ModalNavOnChangeHandler } from './types'

type ModalNavProps<Item extends ModalItem, Group extends ModalGroup> = ComponentProps<'div'> & {
  items: Item[]
  groups: Group[]
  errors?: ModalErrors[]
  onChoose: ModalNavOnChangeHandler<Item>
}

export const ModalNav = <Item extends ModalItem, Group extends ModalGroup>({
  groups,
  items,
  onChoose,
  errors,
  ...rest
}: ModalNavProps<Item, Group>) => {
  const [activeItem, setActiveItem] = useState(items[0])

  const onChooseHandle = useCallback<ModalNavOnChangeHandler<Item>>(
    item => {
      setActiveItem(item)
      onChoose(item)
    },
    [onChoose],
  )

  return (
    <div className={css.navBar} {...rest}>
      {groups.map(({ id, title }) => {
        const itemsByGroup = items.filter(item => item.groupId === id)
        const itemsByGroupIds = itemsByGroup.map(item => item.id)
        const errorsByItemsInGroup = errors?.filter(error =>
          itemsByGroupIds.some(itemId => itemId === error.itemId),
        )

        return (
          <ModalNavGroup
            key={id}
            title={title}
            items={itemsByGroup}
            errors={errorsByItemsInGroup}
            activeItem={activeItem}
            onChoose={onChooseHandle}
          />
        )
      })}
    </div>
  )
}
